import { Clock, Sun, Moon, Sunrise, Sunset, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, CloudFog, Thermometer, Leaf, Snowflake, Flower2, TreePine } from "lucide-react";
import { cn } from "@/lib/utils";

interface WeatherState {
  condition?: string;
  intensity?: string;
  duration_remaining?: number;
}

interface TimeStatusProps {
  hour: number | null;
  day: number | null;
  weather: string | WeatherState | null;
  dayPhase: string | null;
  season?: string | null;
  temperature?: number | null;
  compact?: boolean;
}

const PHASE_ICONS: Record<string, typeof Sun> = {
  dawn: Sunrise,
  morning: Sun,
  afternoon: Sun,
  evening: Sunset,
  night: Moon,
};

const WEATHER_ICONS: Record<string, typeof Sun> = {
  clear: Sun,
  cloudy: Cloud,
  rain: CloudRain,
  storm: CloudLightning,
  snow: CloudSnow,
  blizzard: CloudSnow,
  fog: CloudFog,
  wind: Wind,
  sandstorm: Wind,
  acid_rain: CloudRain,
  spore_fog: CloudFog,
  ion_storm: CloudLightning,
  calm: Cloud,
  aurora: Sun,
};

const SEASON_ICONS: Record<string, typeof Sun> = {
  spring: Flower2,
  summer: Sun,
  autumn: Leaf,
  winter: Snowflake,
};

function getWeatherCondition(weather: string | WeatherState | null): string | null {
  if (!weather) return null;
  if (typeof weather === "string") return weather;
  return weather.condition || null;
}

function getWeatherIntensity(weather: string | WeatherState | null): string | null {
  if (!weather || typeof weather === "string") return null;
  return weather.intensity || null;
}

function getWeatherDuration(weather: string | WeatherState | null): number | null {
  if (!weather || typeof weather === "string") return null;
  return weather.duration_remaining ?? null;
}

function tempColor(temp: number): string {
  if (temp <= -10) return "text-blue-400";
  if (temp <= 0) return "text-blue-300";
  if (temp <= 15) return "text-sky-300";
  if (temp <= 25) return "text-emerald-300";
  if (temp <= 35) return "text-amber-400";
  return "text-red-400";
}

function phaseGradient(phase: string): string {
  switch (phase) {
    case "dawn": return "from-amber-500/10 to-sky-500/10";
    case "morning": return "from-sky-400/10 to-blue-300/10";
    case "afternoon": return "from-yellow-400/10 to-orange-300/10";
    case "evening": return "from-orange-500/15 to-purple-500/10";
    case "night": return "from-indigo-600/15 to-slate-800/10";
    default: return "from-transparent to-transparent";
  }
}

export function TimeStatus({ hour, day, weather, dayPhase, season, temperature, compact }: TimeStatusProps) {
  if (hour == null) return null;

  const phase = dayPhase || (hour < 6 ? "night" : hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening");
  const PhaseIcon = PHASE_ICONS[phase] || Clock;
  const timeStr = `${String(hour).padStart(2, "0")}:00`;
  const weatherCondition = getWeatherCondition(weather);
  const weatherIntensity = getWeatherIntensity(weather);
  const weatherDuration = getWeatherDuration(weather);
  const WeatherIcon = weatherCondition ? WEATHER_ICONS[weatherCondition] || Cloud : null;
  const SeasonIcon = season ? SEASON_ICONS[season] || Leaf : null;

  if (compact !== false) {
    return (
      <div className={cn("flex items-center gap-1.5 text-muted-foreground px-1.5 py-0.5 rounded bg-gradient-to-r", phaseGradient(phase))}>
        <PhaseIcon className="w-3 h-3" />
        <span className="text-foreground font-medium text-xs">{timeStr}</span>
        {day != null && <span className="text-[10px] opacity-60">D{day}</span>}
        {WeatherIcon && (
          <WeatherIcon className={cn("w-3 h-3", weatherIntensity === "severe" ? "text-destructive animate-pulse" : "opacity-70")} />
        )}
        {temperature != null && (
          <span className={cn("text-[10px] font-medium", tempColor(temperature))}>{temperature}°</span>
        )}
        {SeasonIcon && <SeasonIcon className="w-2.5 h-2.5 opacity-50" />}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3 p-3 rounded-lg bg-gradient-to-br border border-border/50", phaseGradient(phase))}>
      {/* Time header */}
      <div className="flex items-center gap-2">
        <PhaseIcon className="w-5 h-5 text-primary" />
        <span className="text-lg font-bold text-foreground">{timeStr}</span>
        <span className="text-sm text-muted-foreground capitalize">{phase}</span>
      </div>

      {/* Day & Season */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {day != null && <span>Day {day}</span>}
        {season && SeasonIcon && (
          <div className="flex items-center gap-1">
            <SeasonIcon className="w-3.5 h-3.5" />
            <span className="capitalize">{season}</span>
          </div>
        )}
      </div>

      {/* Weather */}
      {weatherCondition && WeatherIcon && (
        <div className="flex items-center gap-2 text-xs">
          <WeatherIcon className={cn("w-4 h-4", weatherIntensity === "severe" ? "text-destructive animate-pulse" : "text-muted-foreground")} />
          <span className="capitalize text-foreground">
            {weatherIntensity && weatherIntensity !== "light" ? `${weatherIntensity} ` : ""}
            {weatherCondition.replace(/_/g, " ")}
          </span>
          {weatherDuration != null && (
            <span className="text-muted-foreground/50 text-[10px]">({weatherDuration}h left)</span>
          )}
        </div>
      )}

      {/* Temperature */}
      {temperature != null && (
        <div className="flex items-center gap-1.5 text-xs">
          <Thermometer className={cn("w-3.5 h-3.5", tempColor(temperature))} />
          <span className={cn("font-medium", tempColor(temperature))}>{temperature}°C</span>
          <span className="text-muted-foreground/60 text-[10px]">
            {temperature <= -10 ? "Freezing" : temperature <= 0 ? "Cold" : temperature <= 15 ? "Cool" : temperature <= 25 ? "Mild" : temperature <= 35 ? "Warm" : "Scorching"}
          </span>
        </div>
      )}
    </div>
  );
}
